import { httpSingleton } from '@/lib/singletons/http-singleton';
import { buildBackendDownloadUrl, buildQueryParamUrl } from '@/lib/utils';
import {
	BACKGROUND_COLOR_ID,
	CallToActionBlockRowModel,
	CallToActionFullWidthRowModel,
	CUSTOM_ROW_COLUMN_CONTENT_ORDER_ID,
	CustomRowModel,
	GroupSessionsRowModel,
	MailingListRowModel,
	MailingListEntryTypeId,
	OneColumnImageRowModel,
	OneColumnTextRowModel,
	PAGE_STATUS_ID,
	PAGE_LOCATION_ID,
	PageDetailModel,
	PageLocationModel,
	PageSummaryModel,
	PageFriendlyUrlValidationResult,
	PageRowMailingListModel,
	PageRowUnionModel,
	PageSectionDetailModel,
	PageSectionModel,
	PageSiteLocationModel,
	PresignedUploadResponse,
	ResourcesRowModel,
	ROW_PADDING_ID,
	SITE_LOCATION_ID,
	TagGroupRowModel,
	TagRowModel,
	ThreeColumnImageRowModel,
	TwoColumnImageRowModel,
	TwoColumnTextRowModel,
} from '@/lib/models';

export interface GetPagesResponse {
	totalCountDescription: string;
	totalCount: number;
	pages: PageDetailModel[];
}

export interface GetPublishedPagesResponse {
	pages: PageSummaryModel[];
	totalCount: number;
	totalCountDescription: string;
}

export const pagesService = {
	getSubscriberEmailDownloadUrl(pageId: string) {
		return buildBackendDownloadUrl('/page-row-mailing-lists/csv', {
			pageId,
			mailingListEntryTypeId: MailingListEntryTypeId.EMAIL_ADDRESS,
		});
	},
	getPageLocations() {
		return httpSingleton.orchestrateRequest<{ pageLocations: PageLocationModel[] }>({
			method: 'GET',
			url: '/page-locations',
		});
	},
	getPublishedPages(searchParameters: {
		pageLocationId: PAGE_LOCATION_ID;
		searchQuery?: string;
		pageNumber?: string;
		pageSize?: string;
	}) {
		return httpSingleton.orchestrateRequest<GetPublishedPagesResponse>({
			method: 'GET',
			url: buildQueryParamUrl('/pages/published', searchParameters),
		});
	},
	validatePageUrl(searchParameters?: { searchQuery?: string; pageId?: string }) {
		return httpSingleton.orchestrateRequest<{
			pageUrlNameValidationResult: PageFriendlyUrlValidationResult;
		}>({
			method: 'GET',
			url: buildQueryParamUrl('/pages/validate-url-name', searchParameters),
		});
	},
	createPage(data: {
		name: string;
		urlName: string;
		pageStatusId: PAGE_STATUS_ID;
		pageLocationId?: PAGE_LOCATION_ID;
		headline?: string;
		description?: string;
		imageFileUploadId?: string;
		imageAltText?: string;
	}) {
		return httpSingleton.orchestrateRequest<{
			page: PageDetailModel;
		}>({
			method: 'POST',
			url: '/pages',
			data,
		});
	},
	getPages(searchParameters?: { pageNumber?: string; pageSize?: string; searchQuery?: string; orderBy?: string }) {
		return httpSingleton.orchestrateRequest<GetPagesResponse>({
			method: 'GET',
			url: buildQueryParamUrl('/pages', searchParameters),
		});
	},
	getPageEditId(pageId: string) {
		return httpSingleton.orchestrateRequest<{ pageId: string }>({
			method: 'POST',
			url: `/pages/${pageId}/edit`,
		});
	},
	getPage(pageId: string) {
		return httpSingleton.orchestrateRequest<{ page: PageDetailModel }>({
			method: 'GET',
			url: `/pages/${pageId}`,
		});
	},
	deletePage(pageId: string) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'DELETE',
			url: `/pages/${pageId}`,
		});
	},
	updatePageSettings(
		pageId: string,
		data: {
			name: string;
			urlName: string;
			pageLocationId?: PAGE_LOCATION_ID;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			page: PageDetailModel;
		}>({
			method: 'PUT',
			url: `/pages/${pageId}/settings`,
			data,
		});
	},
	updatePageHero(
		pageId: string,
		data: {
			headline: string;
			description: string;
			imageFileUploadId: string;
			imageAltText: string;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			page: PageDetailModel;
		}>({
			method: 'PUT',
			url: `/pages/${pageId}/hero`,
			data,
		});
	},
	createPageSection(
		pageId: string,
		data: {
			name: string;
			headline?: string;
			description?: string;
			backgroundColorId: BACKGROUND_COLOR_ID;
			displayOrder?: number;
			pageStatusId: PAGE_STATUS_ID;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageSection: PageSectionModel;
		}>({
			method: 'POST',
			url: `/pages/${pageId}/section`,
			data,
		});
	},
	updatePageSection(
		pageSectionId: string,
		data: {
			name: string;
			headline: string;
			description: string;
			backgroundColorId: BACKGROUND_COLOR_ID;
			displayOrder: number;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageSection: PageSectionDetailModel;
		}>({
			method: 'PUT',
			url: `/pages/section/${pageSectionId}`,
			data,
		});
	},
	deletePageSection(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'DELETE',
			url: `/pages/section/${pageSectionId}`,
		});
	},
	reorderPageSections(pageId: string, data: { pageSectionIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageSections: PageSectionDetailModel[];
		}>({
			method: 'PUT',
			url: `/pages/${pageId}/section`,
			data,
		});
	},
	createResourcesRow(pageSectionId: string, data: { contentIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: ResourcesRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/content`,
			data,
		});
	},
	updateResourcesRow(pageRowId: string, data: { contentIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: ResourcesRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/content`,
			data,
		});
	},
	deleteResourcesRowContent(pageRowId: string, contentId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: ResourcesRowModel;
		}>({
			method: 'DELETE',
			url: `/pages/row/${pageRowId}/content/${contentId}`,
		});
	},
	createGroupSessionsRow(pageSectionId: string, data: { groupSessionIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: GroupSessionsRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/group-session`,
			data,
		});
	},
	updateGroupSessionsRow(pageRowId: string, data: { groupSessionIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: GroupSessionsRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/group-session`,
			data,
		});
	},
	deleteGroupSessionsRowGroupSession(pageRowId: string, groupSessionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: GroupSessionsRowModel;
		}>({
			method: 'DELETE',
			url: `/pages/row/${pageRowId}/group-session/${groupSessionId}`,
		});
	},
	createTagGroupRow(pageSectionId: string, data: { tagGroupId: string }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TagGroupRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/tag-group`,
			data,
		});
	},
	updateTagGroupRow(pageRowId: string, data: { tagGroupId: string }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TagGroupRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/tag-group`,
			data,
		});
	},
	createTagRow(pageSectionId: string, data: { tagId: string }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TagRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/tag`,
			data,
		});
	},
	updateTagRow(pageRowId: string, data: { tagId: string }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TagRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/tag`,
			data,
		});
	},
	createCustomRow(
		pageSectionId: string,
		data: {
			name?: string;
			backgroundColorId?: BACKGROUND_COLOR_ID;
			paddingTopId?: ROW_PADDING_ID;
			paddingBottomId?: ROW_PADDING_ID;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CustomRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-row`,
			data,
		});
	},
	createCustomRowColumn(
		pageRowId: string,
		data: {
			headline?: string;
			description?: string;
			imageFileUploadId?: string;
			imageAltText?: string;
			usePlaceholderImage?: boolean;
			contentOrderId?: CUSTOM_ROW_COLUMN_CONTENT_ORDER_ID;
		} = {}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CustomRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageRowId}/custom-row/column`,
			data,
		});
	},
	reorderCustomRowColumns(pageRowId: string, data: { pageRowColumnIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CustomRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-row/column`,
			data,
		});
	},
	updateCustomRowColumn(
		pageRowId: string,
		pageRowColumnId: string,
		data: {
			headline?: string;
			description?: string;
			imageFileUploadId?: string;
			imageAltText?: string;
			usePlaceholderImage?: boolean;
			contentOrderId?: CUSTOM_ROW_COLUMN_CONTENT_ORDER_ID;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CustomRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-row/column/${pageRowColumnId}`,
			data,
		});
	},
	deleteCustomRowColumn(pageRowId: string, pageRowColumnId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CustomRowModel;
		}>({
			method: 'DELETE',
			url: `/pages/row/${pageRowId}/custom-row/column/${pageRowColumnId}`,
		});
	},
	createOneColumnRow(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: OneColumnImageRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-one-column`,
			data: { columnOne: {} },
		});
	},
	updateOneColumnRow(
		pageRowId: string,
		data: {
			columnOne: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: OneColumnImageRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-one-column`,
			data,
		});
	},
	createTwoColumnRow(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TwoColumnImageRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-two-column`,
			data: { columnOne: {}, columnTwo: {} },
		});
	},
	updateTwoColumnRow(
		pageRowId: string,
		data: {
			columnOne: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
			columnTwo: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TwoColumnImageRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-two-column`,
			data,
		});
	},
	createThreeColumnRow(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: ThreeColumnImageRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-three-column`,
			data: { columnOne: {}, columnTwo: {}, columnThree: {} },
		});
	},
	updateThreeColumnRow(
		pageRowId: string,
		data: {
			columnOne: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
			columnTwo: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
			columnThree: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: ThreeColumnImageRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-three-column`,
			data,
		});
	},
	createMailingListRow(pageSectionId: string, data: { pageSectionId: string }) {
		return httpSingleton.orchestrateRequest<{
			pageRow: MailingListRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/mailing-list`,
			data,
		});
	},
	updateMailingListRow(
		pageRowId: string,
		data: { pageRowId: string; mailingListId: string; title: string; description: string }
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: MailingListRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/mailing-list`,
			data,
		});
	},
	createCallToActionBlockRow(
		pageSectionId: string,
		data: {
			headline?: string;
			description?: string;
			buttonText?: string;
			buttonUrl?: string;
			imageFileUploadId?: string;
		} = {}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CallToActionBlockRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/call-to-action-block`,
			data,
		});
	},
	updateCallToActionBlockRow(
		pageRowId: string,
		data: {
			headline: string;
			description: string;
			buttonText: string;
			buttonUrl: string;
			imageFileUploadId?: string;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CallToActionBlockRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/call-to-action-block`,
			data,
		});
	},
	createCallToActionFullWidthRow(
		pageSectionId: string,
		data: {
			headline?: string;
			description?: string;
			buttonText?: string;
			buttonUrl?: string;
		} = {}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CallToActionFullWidthRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/call-to-action-full-width`,
			data,
		});
	},
	updateCallToActionFullWidthRow(
		pageRowId: string,
		data: {
			headline: string;
			description: string;
			buttonText: string;
			buttonUrl: string;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: CallToActionFullWidthRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/call-to-action-full-width`,
			data,
		});
	},
	deletePageRow(pageRowId: string) {
		return httpSingleton.orchestrateRequest<void>({
			method: 'DELETE',
			url: `/pages/row/${pageRowId}`,
		});
	},
	duplicatePageRow(pageRowId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: PageRowUnionModel;
			pageRows: PageRowUnionModel[];
		}>({
			method: 'POST',
			url: `/pages/row/${pageRowId}/duplicate`,
		});
	},
	updatePageRow(
		pageRowId: string,
		data: {
			name: string;
			backgroundColorId: BACKGROUND_COLOR_ID;
			paddingTopId: ROW_PADDING_ID;
			paddingBottomId: ROW_PADDING_ID;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: PageRowUnionModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/settings`,
			data,
		});
	},
	reorderPageSectionRows(pageSectionId: string, data: { pageRowIds: string[] }) {
		return httpSingleton.orchestrateRequest<{
			pageRows: PageRowUnionModel[];
		}>({
			method: 'PUT',
			url: `/pages/row/${pageSectionId}`,
			data,
		});
	},
	createPresignedFileUpload(data: { contentType: string; filename: string }) {
		return httpSingleton.orchestrateRequest<PresignedUploadResponse>({
			method: 'POST',
			url: '/pages/file-presigned-upload',
			data,
		});
	},
	publishPage(pageId: string) {
		return httpSingleton.orchestrateRequest<{
			page: PageDetailModel;
		}>({
			method: 'PUT',
			url: `/pages/${pageId}/publish`,
		});
	},
	unpublishPage(pageId: string) {
		return httpSingleton.orchestrateRequest<{
			page: PageDetailModel;
		}>({
			method: 'PUT',
			url: `/pages/${pageId}/unpublish`,
		});
	},
	duplicatePage(
		pageId: string,
		data: {
			name: string;
			urlName: string;
			copyForEditing: boolean;
		}
	) {
		return httpSingleton.orchestrateRequest<{
			page: PageDetailModel;
		}>({
			method: 'PUT',
			url: `/pages/${pageId}/duplicate`,
			data,
		});
	},
	getPublicPage(pageIdentifier: string) {
		return httpSingleton.orchestrateRequest<{ page: PageDetailModel }>({
			method: 'GET',
			url: `/pages/published/${pageIdentifier}`,
		});
	},
	getPageSiteLocations() {
		return httpSingleton.orchestrateRequest<{ pageSiteLocations: PageSiteLocationModel[] }>({
			method: 'GET',
			url: '/page-site-locations',
		});
	},
	getPageSiteLocationsBySiteLocationId(siteLocationId: SITE_LOCATION_ID) {
		return httpSingleton.orchestrateRequest<{ pageSiteLocations: PageSiteLocationModel[] }>({
			method: 'GET',
			url: `/page-site-locations/${siteLocationId}`,
		});
	},
	getPageRowMailingListsForPageById(pageId: string) {
		return httpSingleton.orchestrateRequest<{ pageRowMailingLists: PageRowMailingListModel[] }>({
			method: 'GET',
			url: `/pages/${pageId}/page-row-mailing-lists`,
		});
	},
	createOneColumnTextRow(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: OneColumnTextRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-one-column-text`,
			data: { columnOne: {} },
		});
	},
	updateOneColumnTextRow(
		pageRowId: string,
		data: {
			columnOne: {
				headline: string;
				description: string;
				imageFileUploadId?: string;
				imageAltText?: string;
			};
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: OneColumnTextRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-one-column-text`,
			data,
		});
	},
	createOneColumnImageRightRow(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: OneColumnImageRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-one-column-right`,
			data: { columnOne: {} },
		});
	},
	updateOneColumnImageRightRow(
		pageRowId: string,
		data: {
			columnOne: {
				headline: string;
				description: string;
				imageFileUploadId: string;
				imageAltText: string;
			};
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: OneColumnImageRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-one-column-right`,
			data,
		});
	},
	createTwoColumnTextRow(pageSectionId: string) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TwoColumnTextRowModel;
		}>({
			method: 'POST',
			url: `/pages/row/${pageSectionId}/custom-two-column-text`,
			data: { columnOne: {}, columnTwo: {} },
		});
	},
	updateTwoColumnTextRow(
		pageRowId: string,
		data: {
			columnOne: {
				headline: string;
				description: string;
				imageFileUploadId?: string;
				imageAltText?: string;
			};
			columnTwo: {
				headline: string;
				description: string;
				imageFileUploadId?: string;
				imageAltText?: string;
			};
		}
	) {
		return httpSingleton.orchestrateRequest<{
			pageRow: TwoColumnTextRowModel;
		}>({
			method: 'PUT',
			url: `/pages/row/${pageRowId}/custom-two-column-text`,
			data,
		});
	},
};
